window.FlexBundles = window.FlexBundles || {};

// Define all custom events in one place
window.FlexBundles.EVENTS = {
  AFTER_ADD_TO_CART: "flex-bundles:after-add-to-cart",
  VARIANT_CHANGED: "flex-bundles:variant-changed",
  PRODUCT_UPDATED: "flex-bundles:product-updated",
  AVAILABILITY_CHANGED: "flex-bundles:availability-changed",
};

window.FlexBundles.initBundle = (blockId) => {
  const node = document.getElementById(`flex-bundles-${blockId}`);
  if (!node) return;

  let data = JSON.parse(node.querySelector("[js-flex-bundles-data]").textContent);
  let bundleConfig = data.config;
  let bundleVariantsData = data.variants;
  let parentProductData = data.parent;

  function getVariantData(productId) {
    return bundleVariantsData[productId] || [];
  }

  // Helper functions for price calculations
  function toCents(dollars) {
    return Math.round(parseFloat(dollars || 0) * 100);
  }

  function toDollars(cents) {
    return (cents / 100).toFixed(2);
  }

  function formatMoney(dollars) {
    return "$" + parseFloat(dollars).toFixed(2);
  }

  // Create a global state object to track selected variants and their prices
  const bundleState = {
    selectedVariants: {},
    quantityInputs: {}, // Cache for quantity inputs

    // Cache a quantity input for a component
    cacheQuantityInput(componentId, input) {
      this.quantityInputs[componentId] = input;
    },

    setVariant(componentId, variant, quantity) {
      if (variant) {

        // For dynamic bundles, use passed quantity
        if (bundleConfig.bundle_type === 'dynamic') {
          variant.quantity = quantity || 1;
        } else {
          // For fixed bundles, get quantity from bundle config
          const component = bundleConfig.components.find(c => c.component_id === componentId);
          variant.quantity = component?.quantity ? parseInt(component.quantity) : 1;
        }
        
        if (bundleConfig && bundleConfig.components) {
          const component = bundleConfig.components.find(c => c.component_id === componentId);
          if (component?.discount) {
            variant.discount = component.discount;
          }
        }
      }

      this.selectedVariants[componentId] = variant;
    },

    // Get the currently selected variant for a product
    getVariant(productId) {
      return this.selectedVariants[productId];
    },

    // Calculate bundle prices based on selected variants
    calculatePrices() {
      let originalPriceCents = 0;
      let discountedPriceCents = 0;

      // First calculate base price based on price_type
      if (bundleConfig?.price_type === "parent") {
        // Use parent product price
        originalPriceCents = parentProductData.price;
        discountedPriceCents = originalPriceCents;
      } else if (bundleConfig?.price_type === "sum") {
        // Sum up prices of all selected variants
        Object.values(this.selectedVariants).forEach((variant) => {
          if (variant && variant.price) {
            
            const quantity = variant.quantity || 1;
            // Convert price to cents before multiplying by quantity
            const variantPriceCents = Math.round(variant.price) * quantity;
            originalPriceCents += variantPriceCents;
          }
        });
        discountedPriceCents = originalPriceCents;
      }

      // Then apply discount based on discount_config type
      const { type: discountType, value: discountValue } =
        bundleConfig?.discount_config || {};

      switch (discountType) {
        case "percentage": {
          const discountPercent = parseFloat(discountValue || 0);
          const discountMultiplier = 1 - discountPercent / 100;
          discountedPriceCents = Math.round(
            originalPriceCents * discountMultiplier,
          );
          break;
        }
        case "fixed": {
          const discountAmountCents = Math.round(discountValue * 100);
          discountedPriceCents = Math.max(
            0,
            originalPriceCents - discountAmountCents,
          );
          break;
        }
        case "percentage-by-product": {
          // Reset discounted price for per-product calculations
          discountedPriceCents = 0;
          Object.entries(this.selectedVariants).forEach(
            ([productId, variant]) => {
              if (variant && variant.price) {
                const quantity = variant.quantity || 1;
                const variantPriceCents = variant.price * quantity;
                const component = bundleConfig.components.find(
                  (c) => c.product_id === productId,
                );
                if (component?.discount) {
                  const discountMultiplier = 1 - component.discount / 100;
                  discountedPriceCents += Math.round(
                    variantPriceCents * discountMultiplier,
                  );
                } else {
                  discountedPriceCents += variantPriceCents;
                }
              }
            },
          );
          break;
        }
        case "progressive-percentage-quantity": {
          const tiers = bundleConfig.progressive_tiers || [];
          const totalQuantity = Object.values(this.selectedVariants).reduce(
            (total, variant) => total + (variant?.quantity || 0),
            0
          );
          
          const matchingTiers = tiers.filter((t) => t?.minimum <= totalQuantity);
          const matchingTier = matchingTiers.reduce((highest, current) => 
            (!highest || current?.minimum > highest?.minimum) ? current : highest, 
            null
          );

          if (matchingTier?.discount) {
            const discountPercent = parseFloat(matchingTier.discount);
            const discountMultiplier = 1 - (discountPercent / 100);
            discountedPriceCents = Math.round(originalPriceCents * discountMultiplier);
          } else {
            discountedPriceCents = originalPriceCents;
          }
          break;
        }
        case "fixed-by-product": {
          // Reset discounted price for per-product calculations
          discountedPriceCents = 0;
          Object.entries(this.selectedVariants).forEach(
            ([productId, variant]) => {
              if (variant && variant.price) {
                const quantity = variant.quantity || 1;
                const variantPriceCents = variant.price * quantity;
                const component = bundleConfig.components.find(
                  (c) => c.product_id === productId,
                );
                if (component?.discount) {
                  const discountCents =
                    Math.round(component.discount * 100) * quantity;
                  discountedPriceCents += Math.max(
                    0,
                    variantPriceCents - discountCents,
                  );
                } else {
                  discountedPriceCents += variantPriceCents;
                }
              }
            },
          );
          break;
        }
        default:
          discountedPriceCents = originalPriceCents;
      }

      // Calculate savings
      const savingsAmountCents = originalPriceCents - discountedPriceCents;
      const savingsPercentage =
        originalPriceCents > 0
          ? Math.round((savingsAmountCents / originalPriceCents) * 100)
          : 0;

      return {
        original: toDollars(originalPriceCents),
        discounted: toDollars(discountedPriceCents),
        savingsAmount: toDollars(savingsAmountCents),
        savingsPercentage,
      };
    },

    // Initialize with default variants
    initialize() {
      if (bundleConfig && bundleConfig.components) {
        bundleConfig.components.forEach((component) => {
          const variants = bundleVariantsData[component.product_id];
          if (variants && variants.length > 0) {
            const sourceVariant = variants.find((v) => v.available) || variants[0];
            
            // Create a new variant object to avoid sharing references
            const firstAvailable = { ...sourceVariant };
            firstAvailable.quantity = parseInt(component.quantity) || 1;

            if (bundleConfig?.bundle_type !== "dynamic") {
              this.setVariant(component.component_id, firstAvailable, firstAvailable.quantity);
            }
          }
        });
      }
    },
  };

  // Update the bundle summary items
  function updateBundleSummary() {
    // Cache DOM elements
    const elements = {
      container: node.querySelector("[js-flex-bundles-summary-items]"),
      subtotalRow: node.querySelector("[js-flex-bundles-summary-subtotal-row]"),
      subtotal: node.querySelector("[js-flex-bundles-summary-subtotal]"),
      discountRow: node.querySelector("[js-flex-bundles-summary-discount-row]"),
      discountLabel: node.querySelector("[js-flex-bundles-discount-label]"),
      discount: node.querySelector("[js-flex-bundles-summary-discount]"),
      total: node.querySelector("[js-flex-bundles-summary-total]")
    };

    if (!elements.container) return;

    // Clear existing content
    elements.container.innerHTML = "";

    // Get selected variants from state
    const selectedVariants = bundleState.selectedVariants;
    if (!Object.keys(selectedVariants).length) return;

    // Build summary items HTML
    const summaryItems = Object.entries(selectedVariants)
      .map(([componentId, variant]) => {
        if (!variant) return null; 

        const productTitle = variant.name || "Product";
        const quantity = variant.quantity || 1;
        const discount = variant.discount || 0;

        // Get variant info
        const variantInfo = variant.public_title === "Default Title" ? "" : variant.public_title;

        // Calculate prices
        const originalItemPrice = variant.price * quantity;
        let discountedItemPrice = originalItemPrice;
        let discountDisplay = "";

        if (bundleConfig.discount_config.type === "percentage-by-product" && discount > 0) {
          const discountMultiplier = 1 - discount / 100;
          discountedItemPrice = originalItemPrice * discountMultiplier;
          discountDisplay = `${discount}% off`;
        } else if (bundleConfig.discount_config.type === "fixed-by-product" && discount > 0) {
          const discountCents = Math.round(discount * 100) * quantity;
          discountedItemPrice = Math.max(0, originalItemPrice - discountCents);
          discountDisplay = `$${discount.toFixed(2)} off`;
        }

        // Build item HTML
        return `
          <div class="flex-bundles__summary-item">
            <div class="flex-bundles__summary-item-title">
              ${productTitle} × ${quantity}
              ${variantInfo ? `<div class="flex-bundles__summary-item-variant">${variantInfo}</div>` : ""}
              ${discount > 0 ? `<div class="flex-bundles__summary-item-discount">${discountDisplay}</div>` : ""}
            </div>
            <div class="flex-bundles__summary-item-price">
              ${discount > 0 ? `<s class="flex-bundles__summary-item-original-price">${formatMoney(originalItemPrice / 100)}</s> ` : ""}
              ${formatMoney(discountedItemPrice / 100)}
            </div>
          </div>
        `;
      })
      .filter(Boolean)
      .join("");

    // Update summary items
    elements.container.innerHTML = summaryItems;

    // Get price calculations
    const prices = bundleState.calculatePrices();

    // Update subtotal
    if (elements.subtotal) {
      elements.subtotal.textContent = formatMoney(prices.original);
    }

    // Handle discount display
    const isByProduct = bundleConfig?.discount_config?.type === "percentage-by-product" || 
                       bundleConfig?.discount_config?.type === "fixed-by-product";
    const hasDiscount = parseFloat(prices.savingsAmount) > 0;

    // Update subtotal row visibility
    if (elements.subtotalRow) {
      elements.subtotalRow.style.display = bundleConfig.discount_config.type === "no-discount" ? "none" : "flex";
    }

    // Update discount row
    if (elements.discountRow) {
      if (hasDiscount) {
        elements.discountRow.style.display = "flex";
        
        if (elements.discountLabel) {
          if (isByProduct) {
            elements.discountLabel.textContent = "Total Savings";
          } else {
            const { type, value } = bundleConfig.discount_config;
            elements.discountLabel.textContent = type === "percentage" 
              ? `Discount (${value}%)`
              : type === "fixed"
                ? `Discount ($${value})`
                : "Discount";
          }
        }

        if (elements.discount) {
          elements.discount.textContent = `-${formatMoney(prices.savingsAmount)}`;
        }
      } else {
        elements.discountRow.style.display = "none";
      }
    }

    // Update total
    if (elements.total) {
      elements.total.textContent = formatMoney(prices.discounted);
    }
  }

  // Update all price displays on the page
  function updatePriceDisplays() {
    const prices = bundleState.calculatePrices();

    // Update original price elements
    node.querySelectorAll("[js-flex-bundles-original-price]").forEach((el) => {
      if (bundleConfig.discount_config.type === "no-discount") {
        el.style.display = "none";
      } else {
        el.style.display = "inline";
        el.textContent = formatMoney(prices.original);
      }
    });

    // Update discounted price elements
    node
      .querySelectorAll("[js-flex-bundles-discounted-price]")
      .forEach((el) => {
        el.textContent = formatMoney(prices.discounted);
      });

    // Update savings percentage elements
    node.querySelectorAll("[js-flex-bundles-savings]").forEach((el) => {
      if (bundleConfig.discount_config.type === "percentage") {
        el.textContent = `Save ${prices.savingsPercentage}%`;
      } else if (bundleConfig.discount_config.type === "fixed") {
        el.textContent = `Save ${formatMoney(prices.savingsAmount)}`;
      } else {
        el.textContent = "";
      }
    });

    // Update the bundle summary
    updateBundleSummary();
  }

  const FlexBundlesProduct = `flex-bundles-product`;
  const FlexBundlesVariantSelects = `flex-bundles-variant-selects`;
  const FlexBundlesForm = `flex-bundles-form`;

  // manages the overall product state and ui
  if (!customElements.get(FlexBundlesProduct)) {
    customElements.define(
      FlexBundlesProduct,
      class extends HTMLElement {
        constructor() {
          super();
          // Store product and component IDs for state management
          this.productId = this.dataset.productId;
          this.componentId = this.dataset.componentId;
          
          // Cache variant data for this product
          this.variantData = getVariantData(this.productId);
          
          // Cache DOM elements for better performance
          this.variantSelects = this.querySelector(FlexBundlesVariantSelects);
          this.priceElement = this.querySelector("[js-flex-bundles-product-price]");
          this.productImage = this.querySelector("[js-flex-bundles-product-image] img");
          this.bundleToggle = this.querySelector("[js-flex-bundles-component-bundle-toggle]");
          this.quantityInput = this.querySelector(`input[name="flex-bundles-component-quantity[${this.productId}]"]`);
          
          // Cache the quantity input in bundleState
          if (this.quantityInput) {
            bundleState.cacheQuantityInput(this.componentId, this.quantityInput);
          }

          // Get initial quantity and discount from bundle config
          const { quantity, discount } = this.getComponentConfig();
          
          // Initialize with the first variant if available
          if (this.variantData?.length > 0) {
            const firstVariant = this.variantData.find(v => v.available) || this.variantData[0];
            // Set initial quantity and discount
            firstVariant.quantity = quantity;
            firstVariant.discount = discount;

            // Update price display with initial values
            if (this.priceElement && firstVariant.price) {
              this.updatePriceDisplay(firstVariant, discount);
            }

            // Update bundle state if component is initially selected
            if (this.bundleToggle?.checked) {
              bundleState.setVariant(this.componentId, firstVariant, quantity);
            }
          }

          // Add a flag to track if we're handling a toggle change
          this.isHandlingToggle = false;

          // Bind methods
          this.onVariantChanged = this.onVariantChanged.bind(this);
        }

        getComponentConfig() {
          if (!bundleConfig?.components) return { quantity: 1, discount: 0 };
          const component = bundleConfig.components.find(c => c.component_id === this.componentId);
          return {
            quantity: bundleConfig.bundle_type === 'dynamic' ? 1 : (component?.quantity || 1),
            discount: component?.discount || 0
          };
        }

        connectedCallback() {
          // Listen for variant changes
          this.addEventListener(FlexBundles.EVENTS.VARIANT_CHANGED, this.onVariantChanged);

          // Listen for quantity changes
          if (this.quantityInput) {
            this.quantityInput.addEventListener("change", this.onQuantityChanged);
          }

          // Listen for bundle toggle changes
          if (this.bundleToggle) {
            this.bundleToggle.addEventListener("change", this.onBundleToggleChanged);
          }
        }

        disconnectedCallback() {
          // Remove event listeners
          this.removeEventListener(FlexBundles.EVENTS.VARIANT_CHANGED, this.onVariantChanged);
          if (this.quantityInput) {
            this.quantityInput.removeEventListener("change", this.onQuantityChanged);
          }
          if (this.bundleToggle) {
            this.bundleToggle.removeEventListener("change", this.onBundleToggleChanged);
          }
        }

        onVariantChanged(event) {
          const variant = event.detail.variant;
          if (!variant) return;

          // Update price display
          if (this.priceElement) {
            const { discount } = this.getComponentConfig();
            this.updatePriceDisplay(variant, discount);
          }

          // Update product image if available
          if (this.productImage && variant.featured_image) {
            this.productImage.src = variant.featured_image.src;
            if (this.productImage.srcset) {
              const currentSrcset = this.productImage.srcset;
              const baseImageUrl = variant.featured_image.src.split('?')[0];
              const newSrcset = currentSrcset.replace(/([^?]*)\?[^ ]*/g, `${baseImageUrl}?`);
              this.productImage.srcset = newSrcset;
            }
          }

          // Only update bundle state if component is selected
          if (this.bundleToggle?.checked) {
            const quantity = this.quantityInput ? parseInt(this.quantityInput.value, 10) : 1;
            bundleState.setVariant(this.componentId, variant, quantity);
            updatePriceDisplays();
            
            // Dispatch product updated event
            this.dispatchEvent(new CustomEvent(FlexBundles.EVENTS.PRODUCT_UPDATED));
          }
        }

        onQuantityChanged = (event) => {
          if (bundleConfig.bundle_type !== 'dynamic') return;
          
          const quantity = parseInt(event.target.value);
          if (isNaN(quantity) || quantity < 1) return;

          const variant = bundleState.selectedVariants[this.componentId];
          if (variant && this.bundleToggle?.checked) {
            bundleState.setVariant(this.componentId, variant, quantity);
            updatePriceDisplays();
          }
        };

        onBundleToggleChanged = (event) => {
          const isChecked = event.target.checked;
          const variant = this.variantSelects?.getSelectedVariant();

          // Single path for updating bundle state
          if (isChecked && variant) {
            const quantity = this.quantityInput ? parseInt(this.quantityInput.value, 10) : 1;
            bundleState.setVariant(this.componentId, variant, quantity);
          } else {
            delete bundleState.selectedVariants[this.componentId];
          }

          // Single update call that handles everything
          updatePriceDisplays(); // This already calls updateBundleSummary internally
        };

        updatePriceDisplay(variant, discount = 0) {
          if (!this.priceElement || !variant?.price) return;
          
          const price = variant.price * (1 - discount / 100);
          this.priceElement.textContent = formatMoney(price  / 100);
        }

        checkAllProductsAvailability() {
          // Implementation of checkAllProductsAvailability method
        }
      }
    );
  }

  // handles variant selection ui and availability
  if (!customElements.get(FlexBundlesVariantSelects)) {
    customElements.define(
      FlexBundlesVariantSelects,
      class extends HTMLElement {
        constructor() {
          super();
          this.productId = this.dataset.productId;
          this.componentId = this.dataset.componentId;
          this.variantData = getVariantData(this.productId);
          this.optionInputs = this.querySelectorAll("[js-flex-bundles-variant-option]");
          this.bundleToggle = this.querySelector("[js-flex-bundles-component-bundle-toggle]");
          this.fieldsets = this.querySelectorAll("[js-flex-bundles-option-fieldset]");
          
          // Cache option positions
          this.optionPositions = [...new Set(
            Array.from(this.optionInputs).map(input => 
              parseInt(input.dataset.optionPosition)
            )
          )];

          // Bind methods once to avoid creating new functions on each event
          this.onToggleChange = this.onToggleChange.bind(this);
          this.onVariantChange = this.onVariantChange.bind(this);
          this.onProductReady = this.onProductReady.bind(this);
        }

        connectedCallback() {
          // Set up event listeners
          if (this.bundleToggle && bundleConfig?.bundle_type === "dynamic") {
            this.bundleToggle.addEventListener("change", this.onToggleChange);
          }
          this.addEventListener("change", this.onVariantChange);
          this.addEventListener('product:ready', this.onProductReady);

          // Initialize UI based on currently checked inputs
          const currentVariant = this.getSelectedVariant();
          if (currentVariant) {
            this.updateOptionLegends(currentVariant.options);
            this.updateOptionsAvailability();
          }
        }

        disconnectedCallback() {
          // Clean up event listeners
          if (this.bundleToggle) {
            this.bundleToggle.removeEventListener("change", this.onToggleChange);
          }
          this.removeEventListener("change", this.onVariantChange);
          this.removeEventListener('product:ready', this.onProductReady);
        }

        onProductReady(event) {
          // Update variant data if provided
          if (event.detail.variantData) {
            this.variantData = event.detail.variantData;
            this.initializeWithFirstVariant();
          }
        }

        initializeWithFirstVariant() {
          const firstVariant = this.variantData.find(v => v.available) || this.variantData[0];
          if (firstVariant) {
            this.initializeWithVariant(firstVariant);
          }
        }

        /**
         * Initializes the component with a specific variant
         * @param {Object} variant - The variant to initialize with
         */
        initializeWithVariant(variant) {
          if (!variant) return;

          // Set initial options
          const options = variant.options;
          this.optionInputs.forEach(input => {
            const position = parseInt(input.dataset.optionPosition) - 1;
            // Uncheck all, then check the matching one
            input.checked = (options[position] === input.value);
          });

          // Update option legends
          this.updateOptionLegends(options);
          // Update option availability
          this.updateOptionsAvailability();
        }

        /**
         * Handles changes to the bundle toggle (for dynamic bundles)
         * @param {Event} event - The change event from the toggle
         */
        onToggleChange(event) {
          const isChecked = event.target.checked;
          const variant = this.getSelectedVariant();
          
          if (!isChecked || !variant) {
            delete bundleState.selectedVariants[this.componentId];
            updatePriceDisplays();
            return;
          }

          // Get current quantity and discount
          let quantity = 1;
          if (bundleConfig.bundle_type === 'dynamic') {
            const quantityInput = bundleState.quantityInputs[this.componentId];
            quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
          }
          
          let discount = 0;
          if (bundleConfig?.components) {
            const component = bundleConfig.components.find(c => c.component_id === this.componentId);
            if (component && (bundleConfig.discount_config.type === "percentage-by-product" || 
                            bundleConfig.discount_config.type === "fixed-by-product")) {
              discount = component.discount || 0;
            }
          }

          // Update variant with quantity and discount
          if (discount > 0) variant.discount = discount;
          
          // Update bundle state and notify parent
          bundleState.setVariant(this.componentId, variant, quantity);
          this.dispatchVariantChanged(variant);
        }

        /**
         * Handles changes to variant options
         */
        onVariantChange() {
          const variant = this.getSelectedVariant();
          if (!variant) return;

          // Update UI and state
          this.updateOptionLegends(variant.options);
          this.updateOptionsAvailability();
          
          // Notify parent of change
          this.dispatchVariantChanged(variant);
        }

        /**
         * Gets the currently selected variant
         * @returns {Object|null} The selected variant or null
         */
        getSelectedVariant() {
          const selectedOptions = this.getSelectedOptions();
          return this.variantData.find(variant =>
            selectedOptions.every((option, index) => option === variant.options[index])
          );
        }

        /**
         * Gets the currently selected options
         * @returns {Array<string>} Array of selected option values
         */
        getSelectedOptions() {
          return Array.from(this.optionInputs)
            .filter(input => input.checked)
            .map(input => ({
              position: parseInt(input.dataset.optionPosition) - 1,
              value: input.value
            }))
            .sort((a, b) => a.position - b.position)
            .map(option => option.value);
        }

        /**
         * Updates which options are available based on current selection
         */
        updateOptionsAvailability() {
          const selectedOptions = this.getSelectedOptions();

          this.optionPositions.forEach(position => {
            const testOptions = [...selectedOptions];
            const optionInputsForPosition = Array.from(this.optionInputs)
              .filter(input => parseInt(input.dataset.optionPosition) === position);

            optionInputsForPosition.forEach(input => {
              testOptions[position - 1] = input.value;
              const isAvailable = this.variantData.some(variant => 
                variant.available && 
                testOptions.every((option, index) => 
                  option === undefined || option === variant.options[index]
                )
              );

              const label = input.nextElementSibling;
              input.disabled = !isAvailable;
              input.classList.toggle("disabled", !isAvailable);
              label?.classList.toggle("disabled", !isAvailable);
            });
          });
        }

        /**
         * Updates the option legends with selected values
         * @param {Array<string>} selectedOptions - Array of selected option values
         */
        updateOptionLegends(selectedOptions) {
          this.fieldsets.forEach((fieldset, index) => {
            const optionValue = fieldset.querySelector("[js-flex-bundles-variant-option-value]");
            if (optionValue && selectedOptions[index]) {
              optionValue.textContent = selectedOptions[index];
            }
          });
        }

        /**
         * Dispatches a variant changed event
         * @param {Object} variant - The variant that changed
         */
        dispatchVariantChanged(variant) {
          this.dispatchEvent(new CustomEvent(FlexBundles.EVENTS.VARIANT_CHANGED, {
            bubbles: true,
            detail: { variant }
          }));
        }
      }
    );
  }

  if (!customElements.get(FlexBundlesForm)) {
    customElements.define(
      FlexBundlesForm,
      class extends HTMLElement {
        constructor() {
          super();
          this.form = this.querySelector("form");
          this.submitButton = this.querySelector("[js-flex-bundles-add-to-cart]");
          this.originalButtonText = this.submitButton?.querySelector("span")?.textContent || "Add to Cart";
          this.blockId = this.dataset.blockId;

          // Only needed for fixed bundles
          this.isFixed = bundleConfig?.bundle_type !== "dynamic";
          if (this.isFixed) {
            this.bundleProducts = Array.from(this.querySelectorAll("[js-flex-bundles-product]"));
          }

          // Bind methods once
          this.handleSubmit = this.handleSubmit.bind(this);
          this.checkAllProductsAvailability = this.checkAllProductsAvailability.bind(this);
          this.onProductUpdated = this.onProductUpdated.bind(this);
        }

        connectedCallback() {
          // Set up event listeners
          if (this.form) {
            this.form.addEventListener("submit", this.handleSubmit, { capture: true });
          }

          this.addEventListener(FlexBundles.EVENTS.PRODUCT_UPDATED, this.onProductUpdated);

          // Check availability immediately since bundleState is already initialized at script level
          this.checkAllProductsAvailability();
        }

        disconnectedCallback() {
          // Clean up event listeners
          if (this.form) {
            this.form.removeEventListener("submit", this.handleSubmit, { capture: true });
          }
          this.removeEventListener(FlexBundles.EVENTS.PRODUCT_UPDATED, this.onProductUpdated);
        }

        /**
         * Handles product update events
         * @param {Event} event - The product updated event
         */
        onProductUpdated(event) {
          updatePriceDisplays();
          this.checkAllProductsAvailability();
        }

        /**
         * Validates the bundle state
         * @returns {Object} Validation result with isValid and error message
         */
        validateBundleState() {
          const selectedVariants = bundleState.selectedVariants;
          
          if (!Object.keys(selectedVariants).length) {
            return { isValid: false, error: "No products selected" };
          }

          // Validate all selected variants
          for (const [componentId, variant] of Object.entries(selectedVariants)) {
            if (!variant) {
              return { isValid: false, error: `Missing variant for component ${componentId}` };
            }

            if (!variant.available) {
              return { isValid: false, error: `Selected variant for component ${componentId} is not available` };
            }

            if (bundleConfig?.bundle_type === 'dynamic' && (!variant.quantity || variant.quantity < 1)) {
              return { isValid: false, error: `Invalid quantity for component ${componentId}` };
            }
          }

          return { isValid: true };
        }

        /**
         * Handles form submission
         * @param {Event} event - The submit event
         */
        handleSubmit(event) {
          event.preventDefault();
          event.stopPropagation();
          if (this.submitButton.disabled) return;

          // Update button state
          updateButtonState(this.submitButton, {
            text: "Adding...",
            loading: true,
            disabled: true,
          });

          const validation = this.validateBundleState();

          if (!validation.isValid) {
            console.error("Bundle validation failed:", validation.error);
            updateButtonState(this.submitButton, {
              text: validation.error,
              loading: false,
              disabled: true,
            });
            return;
          }

          try {
            // Get selected variants from bundleState
            const selectedVariants = Object.entries(bundleState.selectedVariants).map(([componentId, variant]) => ({
              id: variant.id,
              quantity: variant.quantity || 1,
              variantData: variant
            }));

            // Prepare cart properties
            const properties = {
              _components: JSON.stringify(selectedVariants.map((variant) => {
                const componentItem = {
                  id: variant.id,
                  quantity: variant.quantity,
                  attributes: {
                    "Retail Price": formatMoney(variant.variantData.price / 100)
                  }
                };
                return componentItem;
              })),
            };

            // Add line item properties if configured
            if (bundleConfig?.line_item_properties) {
              const prices = bundleState.calculatePrices();
              bundleConfig.line_item_properties.forEach((prop) => {
                properties[prop.key] = prop.value
                  .replace(/\$ORIGINAL_PRICE/g, formatMoney(prices.original))
                  .replace(/\$DISCOUNTED_PRICE/g, formatMoney(prices.discounted))
                  .replace(/\$EXACT_SAVINGS/g, formatMoney(prices.savingsAmount))
                  .replace(/\$PERCENT_SAVINGS/g, `${prices.savingsPercentage}%`);
              });
            }

            Object.entries(bundleState.selectedVariants).forEach(([componentId, variant], index) => {
              console.log(variant);
              properties[`Item ${index + 1}`] = `${variant.name} &times; ${variant.quantity}`;
            });

            // Submit to cart
            handleAddToCart(
              {
                items: [
                  {
                    id: parseInt(bundleConfig.parent.variant_id),
                    quantity: 1,
                    properties,
                  },
                ],
              },
              {
                onSuccess: (data) => {
                  updateButtonState(this.submitButton, {
                    text: "Added!",
                    loading: false,
                    disabled: false,
                  });

                  document.dispatchEvent(
                    new CustomEvent(FlexBundles.EVENTS.AFTER_ADD_TO_CART, {
                      detail: { cartData: data },
                    }),
                  );

                  // Reset button after delay
                  setTimeout(() => {
                    updateButtonState(this.submitButton, {
                      text: this.originalButtonText,
                      loading: false,
                      disabled: false,
                    });
                  }, 2000);
                },
                onError: (errorText) => {
                  updateButtonState(this.submitButton, {
                    text: errorText,
                    loading: false,
                    disabled: false,
                  });
                },
              },
            );
          } catch (error) {
            console.error("Error submitting form:", error);
            updateButtonState(this.submitButton, {
              text: "Error - Try Again",
              loading: false,
              disabled: false,
            });
          }
        }

        /**
         * Checks if all selected products are available
         * @returns {boolean} Whether all products are available
         */
        checkAllProductsAvailability() {
          let allAvailable = true;
          let hasSelections = false;

          // Check all selected variants in bundleState
          Object.values(bundleState.selectedVariants).forEach((variant) => {
            hasSelections = true;
            if (variant && !variant.available) {
              allAvailable = false;
            }
          });

          // Disable button if no selections or if any unavailable
          const shouldDisable = !hasSelections || !allAvailable;

          updateButtonState(this.submitButton, {
            text: !allAvailable ? "Unavailable" : this.originalButtonText,
            loading: false,
            disabled: shouldDisable,
          });

          return allAvailable;
        }
      }
    );
  }

  // Add these helper functions near the top with other helpers
  function handleAddToCart(body, callbacks) {

    console.log('handleAddToCart', body);

    fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "no-cache",
      },
      body: JSON.stringify(body),
    })
      .then((response) => {
        return response.json().then((data) => {
          if (!response.ok) {
            console.error("Cart API error:", data);
            throw new Error(data.description || data.message || `HTTP error! Status: ${response.status}`);
          }
          return data;
        });
      })
      .then((data) => {
        if (data.status) {
          console.error("Error adding products to cart:", data.description);
          callbacks.onError(data.description || "Error - Try Again");
        } else {
          callbacks.onSuccess(data);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        callbacks.onError(error.message || "Error - Try Again");
      });
  }

  function updateButtonState(button, { text, loading, disabled }) {
    const span = button.querySelector("span");
    if (span) span.textContent = text;

    button.disabled = disabled;
    button.setAttribute("data-loading", loading.toString());
    button.setAttribute("aria-disabled", disabled.toString());
  }


  // Initialize immediately since script is at bottom of template
  // Initialize bundleState with server-rendered selections
  node.querySelectorAll('[js-flex-bundles-product]').forEach((productElement) => {
    const componentId = productElement.dataset.componentId;
    const variantSelects = productElement.querySelector('[js-flex-bundles-variant-selects]');
    const bundleToggle = productElement.querySelector('[js-flex-bundles-component-bundle-toggle]');
    const quantityInput = productElement.querySelector(`input[name^="flex-bundles-component-quantity"]`);

    if (variantSelects && bundleToggle?.checked) {
      // Get currently selected variant based on checked radio buttons
      const selectedOptions = Array.from(variantSelects.querySelectorAll('[js-flex-bundles-variant-option]:checked'))
        .map(input => input.value);

      const productId = productElement.dataset.productId;
      const variants = getVariantData(productId);
      const currentVariant = variants.find(variant =>
        selectedOptions.every((option, index) => option === variant.options[index])
      );

      if (currentVariant) {
        const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
        bundleState.setVariant(componentId, currentVariant, quantity);
      }
    }
  });

  updatePriceDisplays();
  updateBundleSummary();

  return () => {
    // Remove event listeners if needed
    node.querySelectorAll("[js-flex-bundles-product]").forEach((product) => {
      product.removeEventListener("variant:changed", product.onVariantChanged);
    });
    // Clear state
    bundleState.selectedVariants = {};
  };
};

if (!customElements.get("flex-bundles-quantity-input")) {
  customElements.define(
    "flex-bundles-quantity-input",
    class extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector("input");
        this.changeEvent = new Event("change", { bubbles: true });

        // Add event listeners
        this.input.addEventListener("change", this.onInputChange.bind(this));
        this.querySelectorAll("button").forEach((button) =>
          button.addEventListener("click", this.onButtonClick.bind(this)),
        );
      }

      // Handle button clicks (plus/minus)
      onButtonClick(event) {
        event.preventDefault();
        const previousValue = this.input.value;

        event.target.name === "plus"
          ? this.input.stepUp()
          : this.input.stepDown();
        if (previousValue !== this.input.value)
          this.input.dispatchEvent(this.changeEvent);
      }

      // Handle direct input changes
      onInputChange() {
        this.validateQtyRules();
      }

      // Validate quantity rules
      validateQtyRules() {
        const value = parseInt(this.input.value);
        if (this.input.min) {
          const min = parseInt(this.input.min);
          const buttonMinus = this.querySelector(
            "[js-flex-bundles-component-quantity-minus]",
          );
          buttonMinus.disabled = value <= min;
        }
        if (this.input.max) {
          const max = parseInt(this.input.max);
          const buttonPlus = this.querySelector(
            "[js-flex-bundles-component-quantity-plus]",
          );
          buttonPlus.disabled = value >= max;
        }
      }
    },
  );
}
